import { ASSIST_MAIL, urlParams } from "../../constants/AppConstants";
import Link from "../../components/custom/info/Link";
import HNotification from "../../components/custom/notification/HNotification";
import { showBrowserNotification } from "./CommonNotifications";

export const httpsUrlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;

export const SAMPLE_CSV_FILE_URL = "https://open-usage-bucket.s3.amazonaws.com/Sample_Sheet_.csv";

export const HOMEPAGE_URL = "/inbox";

export const FB_API_VERSION = "v21.0";
export const FB_SDK_URL = "https://connect.facebook.net/en_US/sdk.js";

export const PATTERN_FOR_MATCHING_CURLY_BRACED_VARIABLES = /(\{\{.*?\}\})/;

export const MAX_INPUT_LENGTH_FOR_FRIENDLY_NAME = 120;

export const OTP_MODAL_EXCLUDE_PATHS = ["/login", "/logout", "/zoho-crm/landing", "/teams/landing", "/zendesk/landing" , "/salesforce/landing"];

export const ACCOUNT_OWNER_ROLE_ID = 11;

export const TEMPLATE_VISIBILITY = {
  PRIVATE: {
    value: 1,
    label: "Only me",
  },
  TEAM: {
    value: 2,
    label: "All users",
  },
}

export const getTemplateVisibilityObjectByValue = (value) => {
  if (!value) return TEMPLATE_VISIBILITY.TEAM;
  return Object.values(TEMPLATE_VISIBILITY).find((item) => item.value === parseInt(value)) || TEMPLATE_VISIBILITY.TEAM;
};

export const deselectCurrentContact = (navigate, location) => {
  navigate({ pathname: "/inbox", search: location.search }, { replace: true });
};

export const PhoneNumberAccessEnum = {
  SYSTEM_DEFAULT: 0,
  ALL_NUMBERS: 1,
  ASSIGNED_NUMBERS: 2,
  TEAM_ASSIGNED_NUMBERS: 3,
};

function capitalizeFirstLetter(str) {
  if (!str) return str;
  str = str?.toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getDefaultUnknownErrorMessageContent(prefix) {
  prefix = prefix ?? "Something went wrong!";
  return `${prefix} Please contact support ${ASSIST_MAIL}`;
}

/**
 * Redirects the user during onboarding if the onBoardingPage parameter exists and during first number enable.
 * Cleans up URL query parameters to remove sideBar and topBar.
 */
export function handleOnboardingRedirect(location, navigate, searchParams, hasSavedNumbers) {
  const onBoardingPageParam = searchParams.get("onBoardingPage") === "true";

  if (onBoardingPageParam && hasSavedNumbers) {
    searchParams.delete("sideBar");
    searchParams.delete("topBar");

    const updatedUrl = `${location.pathname}?${searchParams.toString()}`;
    navigate(updatedUrl, { replace: true });
  }
}

function getRandomNumber(numberOfDigits) {
  if (numberOfDigits < 1) {
    throw new Error("Number of digits must be at least 1");
  }

  const upperBound = Math.pow(10, numberOfDigits) - 1;
  const lowerBound = Math.pow(10, numberOfDigits - 1);

  return Math.floor(Math.random() * (upperBound - lowerBound + 1)) + lowerBound;
}

function mapToMediaList(mediaFiles, hideCloseIcon) {
  return mediaFiles?.map((file) => ({
    name: file.name ?? "file",
    url: file.url,
    size: file.size ?? -1,
    contentType: file.contentType ?? "dummy/dummy",
    type: file.contentType ?? "dummy/dummy",
    thumbnailUrl: file.thumbnailUrl || file.url,
    status: "done",
    uid: getRandomNumber(3),
    hideCloseIcon: hideCloseIcon,
  }));
}

function checkWhetherServiceIsAuthorized(appProperties, integId, serviceObject, isLeft) {
  if (!serviceObject) {
    serviceObject = getServiceObjectFromIntegId(appProperties, integId);
  }
  if (isLeft) {
    return serviceObject?.left?.auth?.authorized;
  } else {
    return serviceObject?.right?.auth?.authorized;
  }
}

function getServiceObjectFromIntegId(appProperties, integId) {
  return appProperties?.installedApps?.find((obj) => obj?.integProps?.integId === integId);
}

function openServiceNotAuthorizedModal(serviceObject, appProperties) {
  const matchedPhoneAccess = appProperties?.licenseObj?.allLicensedUserDetails?.find(
    (user) => user?.remoteUserId === appProperties?.userId
  )?.phoneAccess;

  if (matchedPhoneAccess === 0) {
    let redirectionUrl = `/channels/textsms/${serviceObject?.service_name}?`;
    appProperties?.controller?.getUrlParamsForRedirection(true, appProperties).then(function (redirectionUrlFromApp) {
      if (redirectionUrlFromApp) {
        redirectionUrl += redirectionUrlFromApp;
      } else {
        redirectionUrl += urlParams;
      }
      HNotification.notify({
        type: "warning",
        message: "Service not authorized",
        description: `${serviceObject?.service_display_name} service is not authorized. Please proceed to authorize.`,
        button: true,
        buttonText: "Authorize",
        buttonLink: redirectionUrl
      });
    });
  }
}


const toneOptions = [
  { key: "1", emoji: "👔", tone: "Professional", description: "Formal and business-appropriate" },
  { key: "2", emoji: "😊", tone: "Friendly", description: "Warm and approachable" },
  { key: "3", emoji: "🌟", tone: "Enthusiastic", description: "Excited and engaging" },
  { key: "4", emoji: "🤝", tone: "Supportive", description: "Encouraging and helpful" },
];

const getToneSuggestionItems = (handleClick) =>
  toneOptions.map(({ key, emoji, tone, description }) => ({
    key,
    label: (
      <div className="d-flex gap-3 pt-2" data-cy={`tone-suggestion-item-${tone.toLowerCase()}`} onClick={() => handleClick(tone)}>
        <div className="hs-fs-20">{emoji}</div>
        <div>
          <div className="hs-fw-500 hs-color-BlackRussian">{tone}</div>
          <div className="hs-color-light-grey-2 hs-fs-12">{description}</div>
        </div>
      </div>
    ),
  }));

export const checkIsLicensedByLicenseStatus = (licenseStatus) => {
  return licenseStatus < 10;
};

export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};

function checkIsAnyAppIsAuthorized(appProperties) {
  return appProperties?.installedApps?.some(checkWhetherAnAppIsAuthorized);
}

function checkWhetherAnAppIsAuthorized(app) {
  return app?.right?.auth?.authorized;
}

function getAddTemplateUrl(appProperties, moduleName) {
  return new Promise((resolve, reject) => {
    appProperties?.controller?.getUrlParamsForRedirection(true, appProperties).then((redirectionUrl) => {
      if (moduleName) {
        resolve("/templates?" + redirectionUrl + "&resource=" + (moduleName ?? "") + "&openTemplate=true");
      } else {
        appProperties?.controller?.getAssociatedObjectType(appProperties).then(function (moduleName) {
          resolve("/templates?" + redirectionUrl + "&resource=" + (moduleName ?? "") + "&openTemplate=true");
        });
      }
    });
  });
}

function openTemplateUrlInBlankPage(appProperties, moduleName) {
  getAddTemplateUrl(appProperties, moduleName).then((link) => {
    if (link) {
      window.open(link, "_blank");
    }
  });
}

const checkTruthy = (object) => {
  if (!object) {
    return false;
  }
  if (typeof object == "object") {
    return Object.keys(object).length > 0;
  }
};

const getRegexForFindingPlaceHolderWithDigitsOnly = () => {
  return /\{\{\d+\}\}/g;
};

const navigateToPathWithCurrentParams = (pathToNavigate, navigate) => {
  const urlParams = window.location.search;
  navigate(`${pathToNavigate}${urlParams}`);
};

const mergeObjectsRetainingExisting = (main, sub) => {
  return {
    ...sub,
    ...Object.fromEntries(Object.entries(main).map(([key, value]) => [key, value ?? sub[key]])),
  };
};

const formatPhoneNumber = (formattedPhoneNumber) => {
  if (!formattedPhoneNumber || typeof formattedPhoneNumber !== "string") return formattedPhoneNumber;
  return formattedPhoneNumber.includes(":::") ? formattedPhoneNumber.split(":::")[1] : formattedPhoneNumber;
};

const checkUserHasModuleAccess = (currentRoleDetails, key) => {
  if (!currentRoleDetails) {
    return true;
  }
  // Normalize the key to lowercase
  const normalizedKey = key.toLowerCase();
  // Find the actual key in the object that matches the normalized key
  const actualKey = Object.keys(currentRoleDetails).find((k) => k.toLowerCase() === normalizedKey);
  // Check if the key exists and has access
  return actualKey ? currentRoleDetails[actualKey]?.hasAccess !== false : false;
};

const getSettingBySettingName = (appProperties, settingName) => {
  return appProperties?.settings?.find((obj) => obj?.settingName_leftServiceId?.includes(settingName));
};

const checkUserHasTeamManageAccess = (rolesBasedObject, key) => {
  if (!rolesBasedObject) {
    return true;
  }
  const normalizedKey = key.toLowerCase();
  const actualKey = Object.keys(rolesBasedObject).find((k) => k.toLowerCase() === normalizedKey);
  return actualKey ? rolesBasedObject[actualKey]?.permissionLevel === 2 && rolesBasedObject[actualKey]?.fullAccess : false;
};

const checkUserHasEditAccess = (currentRoleDetails, key) => {
  if (!currentRoleDetails || !key) {
    return true;
  }
  const moduleDetails = currentRoleDetails?.[key.toLowerCase()];
  return moduleDetails?.permissionLevel >= 2;
};

const checkUserHasFullAccess = (currentRoleDetails, key) => {
  if (!currentRoleDetails || !key) {
    return true;
  }
  const moduleDetails = currentRoleDetails?.[key.toLowerCase()];
  return moduleDetails?.permissionLevel >= 2 && moduleDetails?.fullAccess;
};

const checkIsAdmin = (appProperties, rolesProperties) => {
  const currentUserObj = appProperties?.licenseObj?.allLicensedUserDetails?.find((user) => user?.remoteUserId === appProperties?.userId);
  const roleId = currentUserObj?.roleId?.toString();
  const rolesBasedObject = rolesProperties?.rolesBasedObject || {};
  if (!rolesBasedObject) return false;
  const adminRoleKey = Object.entries(rolesBasedObject).find(([_, value]) => value.toLowerCase().includes("admin"))?.[0];
  const ownerRoleKey = Object.entries(rolesBasedObject).find(([_, value]) => value.toLowerCase().includes("owner"))?.[0];
  return roleId === adminRoleKey || roleId === ownerRoleKey;
};

const checkIsUserRole = (appProperties, rolesProperties) => {
  const currentUserObj = appProperties?.licenseObj?.allLicensedUserDetails?.find((user) => user?.remoteUserId === appProperties?.userId);
  const roleId = currentUserObj?.roleId?.toString();
  const rolesBasedObject = rolesProperties?.rolesBasedObject || {};
  if (!rolesBasedObject) return false;
  const userRoleKey = Object.entries(rolesBasedObject).find(([_, value]) => value.toLowerCase().includes("user"))?.[0];
  return roleId === userRoleKey;
};

const checkCurrentUserHasAccountOwnerRolePermission = (appProperties, rolesProperties) => {
  const currentUserObj = appProperties?.licenseObj?.allLicensedUserDetails?.find((user) => user?.remoteUserId === appProperties?.userId);
  const roleId = currentUserObj?.roleId?.toString();
  const rolesBasedObject = rolesProperties?.rolesBasedObject || {};
  if (!rolesBasedObject) return false;
  const ownerRoleKey = Object.entries(rolesBasedObject).find(([_, value]) => value.toLowerCase().includes("owner"))?.[0];
  return roleId === ownerRoleKey;
};

const getAccountOwnerId = (appProperties, rolesProperties) => {
  const accountOwnerObj = appProperties?.licenseObj?.allLicensedUserDetails?.find((user) => user?.roleId === 11);
  if (accountOwnerObj) {
    return accountOwnerObj?.remoteUserId;
  }
  return null;
};
const getAccountOwnerEmail = (appProperties, rolesProperties) => {
  const accountOwnerObj = appProperties?.licenseObj?.allLicensedUserDetails?.find((user) => user?.roleId === 11);
  if (accountOwnerObj) {
    return accountOwnerObj?.email;
  }
  return null;
};

const getCurrentUserDetails = (appProperties) => {
  const userId = appProperties?.userId;
  const userDetails = appProperties?.licenseObj?.allLicensedUserDetails;
  return userDetails?.find((user) => user?.remoteUserId === userId);
};

const checkUserHasChannelAccess = (appProperties) => {
  const currentUser = appProperties?.currentUser;
  if (!currentUser) return false;
  const channelAccess = currentUser?.phoneAccess;
  return channelAccess === 0;
};

const calculatePercentile = (processedCount, totalCount) => {
  const processed = parseInt(processedCount, 10);
  const total = parseInt(totalCount, 10);
  if (isNaN(processed) || isNaN(total) || total === 0) return 0;
  return Math.round((processed / total) * 100);
};

const checkTheAccountHasAdminRole = (appProperties, rolesProperties, emailId) => {
  const currentUserObj = appProperties?.licenseObj?.allLicensedUserDetails?.find((user) => user?.email === emailId);
  const roleId = currentUserObj?.roleId?.toString();
  const rolesBasedObject = rolesProperties?.rolesBasedObject || {};
  if (!rolesBasedObject) return false;
  const adminRoleKey = Object.entries(rolesBasedObject).find(([_, value]) => value.toLowerCase().includes("admin"))?.[0];
  const ownerRoleKey = Object.entries(rolesBasedObject).find(([_, value]) => value.toLowerCase().includes("owner"))?.[0];
  return roleId === adminRoleKey || roleId === ownerRoleKey;
};

const checkIsInsideIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

const getHighUsageAlertMessage = (serivceDisplayName, phoneNumber) => {
  var message = (
    <>
      <span>
        ⚠️ Warning: High {serivceDisplayName} usage detected{phoneNumber ? ` for ${formatPhoneNumber(phoneNumber)}` : ""}. Further sending may
        lead to a temporary block.
      </span>
      <Link
        baseUrl="https://docs.360dialog.com/docs/whatsapp-marketing/best-practices-to-maintain-account-health-and-prevent-being-blocked"
        externalLink={true}
      >
        Learn more
      </Link>
    </>
  );
  return message;
};

const SERVICE_ID_TO_INFO_MAP = {};

const findServiceInfoByServiceId = (appProperties, serviceId) => {
  if (!appProperties || !appProperties.apps || !serviceId) return null;
  if (SERVICE_ID_TO_INFO_MAP[serviceId]) {
    return SERVICE_ID_TO_INFO_MAP[serviceId];
  }

  for (const key in appProperties.apps) {
    const found = appProperties.apps[key].find((item) => item.serviceId === serviceId);
    if (found) {
      SERVICE_ID_TO_INFO_MAP[serviceId] = found;
      return found;
    }
  }
  return null;
};

const SERVICE_NAME_TO_INFO_MAP = {};

const findServiceInfoByServiceName = (appProperties, serviceName) => {
  if (!appProperties || !appProperties.apps || !serviceName) return null;
  if (SERVICE_NAME_TO_INFO_MAP[serviceName]) {
    return SERVICE_NAME_TO_INFO_MAP[serviceName];
  }
  for (const key in appProperties.apps) {
    const found = appProperties.apps[key].find((item) => item.name === serviceName);
    if (found) {
      SERVICE_NAME_TO_INFO_MAP[serviceName] = found;
      return found;
    }
  }
  return null;
};

function mapMessageLogs(messages) {
  return messages.map(message => {
    return {
      ...message,
      key: message.osyncId || message.messageId || message.logTime || Math.random().toString(36).substr(2, 9),
      createdTime: message.createdTime || message.logTime || message.sentTime || message.timestamp || new Date().toISOString(),
      status: (message.messageStatus || message.status || '').toLowerCase(),
      sender: message.sender || message.from,
      receiver: message.receiver || message.to,
      messageBody: message.message || message.messageBody || message.content || message.body || '',
      userName: message.senderName || message.userName || message.user || message.sentBy || '',
      provider: message.serviceName || message.provider || message.channel || message.service || '',
      sent_via: message.sent_via,
      errorCode: message.errorCode || message.error || '',
      statusDetails: message.statusDetails || message.errorDetails || message.errorDescription || '',
      mediaFileList: message.mediaFileList || [],
    };
  });
}

// This function is used to handle the submit shortcut for the notes editor (mac: (cmd + enter or shift + enter), windows: shift + enter)
const handleSubmitShortcut = (e, handleSave) => {
  if (e.key === "Enter" && (e.shiftKey || e.metaKey)) {
    e.preventDefault();
    handleSave();
  }
};

const getInitials = (firstName, lastName) => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  } else if (firstName) {
    return firstName.charAt(0).toUpperCase();
  } else if (lastName) {
    return lastName.charAt(0).toUpperCase();
  }
  return "?";
};

const getHyperLinkForGoToButton = (leftServiceName, appProperties) => {
  if (leftServiceName === "Zoho CRM") {
    return appProperties.domain;
  } else if (leftServiceName === "Hubspot") {
    return appProperties?.controller?.domainToRedirect;
  } else if (leftServiceName === "Teams") {
    return "https://teams.microsoft.com/l/entity/76470f4d-1718-4877-b9c5-8e2bacca55af/61f234af-e714-40ab-b2d6-621b75b4d71f";
  } else {
    return appProperties.domain + (appProperties?.controller?.redirectPath || "");
  }
};

const throwRequiredParam = (param) => {
  throw new Error("Params is required:" + param);
};


const getServiceIconClass = (serviceName) => {
  if (!serviceName) return null;
  const serviceNameLower = serviceName.toLowerCase();
  const supportedServices = [
    "twilio",
    "ringcentral",
    "microsoft-teams",
    "hellosend",
    "whatsapp",
    "unipile_whatsapp",
    "vonage",
    "telnyx",
    "plivo",
    "burstsms",
    "clicksend",
    "zoom",
  ];

  if (supportedServices.includes(serviceNameLower)) {
    return `staticIconsSprite smallSize-${serviceNameLower}-icon`;
  }
  return null;
};

export {
  getInitials,
  handleSubmitShortcut,
  findServiceInfoByServiceId,
  capitalizeFirstLetter,
  getToneSuggestionItems,
  checkIsAnyAppIsAuthorized,
  checkWhetherAnAppIsAuthorized,
  getDefaultUnknownErrorMessageContent,
  openServiceNotAuthorizedModal,
  checkWhetherServiceIsAuthorized,
  getServiceObjectFromIntegId,
  getRandomNumber,
  mapToMediaList,
  openTemplateUrlInBlankPage,
  getAddTemplateUrl,
  checkTruthy,
  getRegexForFindingPlaceHolderWithDigitsOnly,
  navigateToPathWithCurrentParams,
  mergeObjectsRetainingExisting,
  formatPhoneNumber,
  checkUserHasModuleAccess,
  checkUserHasEditAccess,
  checkIsAdmin,
  getCurrentUserDetails,
  checkUserHasFullAccess,
  checkUserHasChannelAccess,
  checkCurrentUserHasAccountOwnerRolePermission,
  getAccountOwnerId,
  getAccountOwnerEmail,
  calculatePercentile,
  checkTheAccountHasAdminRole,
  checkIsInsideIframe,
  getHighUsageAlertMessage,
  mapMessageLogs,
  checkUserHasTeamManageAccess,
  getHyperLinkForGoToButton,
  throwRequiredParam,
  checkIsUserRole,
  getServiceIconClass,
  findServiceInfoByServiceName,
  getSettingBySettingName
};
